/**
 * Display formatting.
 *
 * The API speaks paise (integers) end to end. Conversion to rupees happens
 * HERE and only here — nothing in the app does arithmetic on a rupee value,
 * because that is how rounding errors get into a total the customer reads.
 */

import type { Locale } from './i18n';
import { isDialable } from './phone';

/** Indian-grouped currency: ₹1,23,456.78 */
export function money(paise: number | null | undefined, opts?: { symbol?: boolean; compact?: boolean }): string {
  if (paise === null || paise === undefined) return '—';
  const symbol = opts?.symbol !== false;
  const negative = paise < 0;
  const abs = Math.abs(paise);

  if (opts?.compact) {
    const rupees = abs / 100;
    // Lakh and crore, because that is how the numbers are spoken here.
    if (rupees >= 10_000_000) return `${negative ? '-' : ''}${symbol ? '₹' : ''}${(rupees / 10_000_000).toFixed(2)} Cr`;
    if (rupees >= 100_000) return `${negative ? '-' : ''}${symbol ? '₹' : ''}${(rupees / 100_000).toFixed(2)} L`;
    if (rupees >= 1000) return `${negative ? '-' : ''}${symbol ? '₹' : ''}${(rupees / 1000).toFixed(1)}K`;
  }

  const whole = Math.floor(abs / 100);
  const fraction = abs % 100;
  const s = String(whole);

  let grouped: string;
  if (s.length <= 3) {
    grouped = s;
  } else {
    // Indian grouping: last three digits, then pairs.
    const last3 = s.slice(-3);
    const rest = s.slice(0, -3);
    grouped = `${rest.replace(/\B(?=(\d{2})+(?!\d))/g, ',')},${last3}`;
  }

  return `${negative ? '-' : ''}${symbol ? '₹' : ''}${grouped}.${String(fraction).padStart(2, '0')}`;
}

/** Rupees typed by a human → paise. Rounds, never truncates. */
export function toPaise(rupees: string | number): number {
  const n = typeof rupees === 'string' ? parseFloat(rupees.replace(/[^\d.-]/g, '')) : rupees;
  if (!Number.isFinite(n)) return 0;
  return Math.round(n * 100);
}

/** Paise → a plain number for an <input type="number">. */
export function toRupeeInput(paise: number | null | undefined): string {
  if (paise === null || paise === undefined) return '';
  return (paise / 100).toFixed(2);
}

// ---------------------------------------------------------------------------
// Dates
// ---------------------------------------------------------------------------

const IST_OFFSET_MIN = 330;

/** 'YYYY-MM-DD' in IST — the business date, matching the server. */
export function businessDate(at: Date = new Date()): string {
  return new Date(at.getTime() + IST_OFFSET_MIN * 60_000).toISOString().slice(0, 10);
}

/** 'DD-MM-YYYY' for display. */
export function dateShort(iso: string | null | undefined): string {
  if (!iso) return '—';
  const d = iso.length === 10 ? iso : new Date(iso).toISOString().slice(0, 10);
  const [y, m, day] = d.split('-');
  return `${day}-${m}-${y}`;
}

/** '7 Sep 2026' / '7 सित 2026' */
export function dateLong(iso: string | null | undefined, locale: Locale = 'en'): string {
  if (!iso) return '—';
  const date = new Date(iso.length === 10 ? `${iso}T00:00:00Z` : iso);
  return date.toLocaleDateString(locale === 'hi' ? 'hi-IN' : 'en-IN', {
    day: 'numeric', month: 'short', year: 'numeric', timeZone: 'Asia/Kolkata',
  });
}

/** '2:15 pm' in IST. */
export function timeShort(iso: string | null | undefined): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleTimeString('en-IN', {
    hour: 'numeric', minute: '2-digit', hour12: true, timeZone: 'Asia/Kolkata',
  }).toLowerCase();
}

/**
 * Relative time — 'just now', '5 min ago', '2 days ago'.
 *
 * Used on lists where the exact timestamp is noise but recency matters, such as
 * the approvals queue.
 */
export function relative(iso: string | null | undefined, locale: Locale = 'en'): string {
  if (!iso) return '—';
  const diffMs = Date.now() - Date.parse(iso);
  const mins = Math.floor(diffMs / 60_000);

  if (locale === 'hi') {
    if (mins < 1) return 'अभी';
    if (mins < 60) return `${mins} मिनट पहले`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return `${hrs} घंटे पहले`;
    const days = Math.floor(hrs / 24);
    if (days < 30) return `${days} दिन पहले`;
    return dateShort(iso);
  }

  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins} min ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs} hr ago`;
  const days = Math.floor(hrs / 24);
  if (days < 30) return `${days} day${days === 1 ? '' : 's'} ago`;
  return dateShort(iso);
}

/** Whole days between two dates, for ageing. */
export function daysBetween(fromIso: string, toIso?: string): number {
  const from = Date.parse(fromIso.length === 10 ? `${fromIso}T00:00:00Z` : fromIso);
  const to = toIso ? Date.parse(toIso.length === 10 ? `${toIso}T00:00:00Z` : toIso) : Date.now();
  return Math.floor((to - from) / 86_400_000);
}

/** Hours remaining until an ISO instant. Negative once past. */
export function hoursUntil(iso: string | null | undefined): number {
  if (!iso) return 0;
  return Math.round((Date.parse(iso) - Date.now()) / 3_600_000);
}

// ---------------------------------------------------------------------------
// Deep links — no Maps API, so these cost nothing
// ---------------------------------------------------------------------------

/** Opens the installed Google Maps app at the pin. */
export function mapsLink(lat: number | null | undefined, lng: number | null | undefined): string | null {
  if (lat === null || lat === undefined || lng === null || lng === undefined) return null;
  return `https://www.google.com/maps/search/?api=1&query=${lat},${lng}`;
}

/** Turn-by-turn directions to the pin. */
export function directionsLink(lat: number | null | undefined, lng: number | null | undefined): string | null {
  if (lat === null || lat === undefined || lng === null || lng === undefined) return null;
  return `https://www.google.com/maps/dir/?api=1&destination=${lat},${lng}`;
}

/**
 * Native dialer.
 *
 * Returns null for anything that is not a real mobile. Not every row in
 * `users.phone` is a number — a customer with none on file holds a
 * placeholder, because the column is NOT NULL and is the login identity.
 *
 * This used to strip the non-digits and dial whatever was left, so a shop
 * imported from Business Central with no number gave a Call button that
 * produced `tel:+9100347`. A button that rings the wrong person is worse
 * than no button, so the caller shows none.
 */
export function telLink(phone: string | null | undefined): string | null {
  if (!isDialable(phone)) return null;
  return `tel:${phone!.replace(/[^\d+]/g, '')}`;
}

/**
 * WhatsApp chat, for staff contacting a shop outside the app.
 *
 * Same rule as `telLink`: a placeholder is not a number, and a wa.me link
 * built from one opens a chat with whoever those stray digits belong to.
 */
export function whatsappLink(phone: string | null | undefined, text?: string): string | null {
  if (!isDialable(phone)) return null;
  const digits = phone!.replace(/[^\d]/g, '');
  const q = text ? `?text=${encodeURIComponent(text)}` : '';
  return `https://wa.me/${digits}${q}`;
}

// ---------------------------------------------------------------------------
// Misc
// ---------------------------------------------------------------------------

/** +919876543210 → +9198****3210 */
export function maskPhone(phone: string | null | undefined): string {
  if (!phone || phone.length < 8) return '—';
  return `${phone.slice(0, 5)}****${phone.slice(-4)}`;
}

export function initials(name: string | null | undefined): string {
  if (!name) return '?';
  return name.trim().split(/\s+/).slice(0, 2).map((w) => w[0]?.toUpperCase() ?? '').join('');
}

/** 1234567 → '1.2 MB' */
export function bytes(n: number | null | undefined): string {
  if (!n) return '0 KB';
  if (n < 1024) return `${n} B`;
  if (n < 1048576) return `${Math.round(n / 1024)} KB`;
  return `${(n / 1048576).toFixed(1)} MB`;
}

export function percent(n: number | null | undefined, digits = 0): string {
  if (n === null || n === undefined) return '—';
  return `${n.toFixed(digits)}%`;
}

/** Ageing bucket label from a day count. */
export function ageingBucket(days: number): '0-15' | '16-30' | '31-60' | '60+' {
  if (days <= 15) return '0-15';
  if (days <= 30) return '16-30';
  if (days <= 60) return '31-60';
  return '60+';
}

// ---------------------------------------------------------------------------
// Pieces and packs
// ---------------------------------------------------------------------------

/**
 * How a product's pack is defined, as every screen receives it.
 *
 * `packQty` is pieces per pack — 24 for a 1X24 carton. Null means Business
 * Central publishes no unit larger than the base for that item, which is a
 * real answer for something sold only loose, and is NEVER to be read as 1.
 */
export interface PackInfo {
  /** The base unit BC counts in. 'PCS' throughout this catalogue. */
  baseUom?: string | null;
  /** The pack unit's code, e.g. 'BOXS'. */
  packUom?: string | null;
  /** Pieces per pack. */
  packQty?: number | null;
}

export interface PackBreakdown {
  pieces: number;
  /** Whole packs. Zero when the quantity does not fill one. */
  packs: number;
  /** Pieces left over after the whole packs. */
  loose: number;
  /** True when a conversion exists and is greater than one. */
  convertible: boolean;
  packUom: string;
  baseUom: string;
}

/**
 * Split a piece count into packs and loose pieces.
 *
 * WHY THIS IS ONE FUNCTION AND NOT FOUR
 *
 * The same conversion is shown on the stock screen, the catalogue, the order
 * form and the invoice. Four copies of `Math.floor(qty / packQty)` is four
 * chances for one of them to round differently, and the symptom would be two
 * screens quoting different stock for the same product with no error anywhere.
 *
 * `packQty > 1` is the test for convertibility, not `packQty != null`. A pack
 * of one is not a pack: it would let the app offer "1 box" for a single bottle
 * and let somebody order a box meaning twenty-four.
 */
export function packBreakdown(pieces: number, pack: PackInfo): PackBreakdown {
  const qty = pack.packQty ?? 0;
  const baseUom = (pack.baseUom || 'pcs').toLowerCase();
  const packUom = (pack.packUom || 'box').toLowerCase();
  const convertible = qty > 1 && Number.isFinite(pieces);

  if (!convertible) {
    return { pieces, packs: 0, loose: pieces, convertible: false, packUom, baseUom };
  }
  return {
    pieces,
    packs: Math.floor(pieces / qty),
    loose: Math.round(pieces % qty),
    convertible: true,
    packUom,
    baseUom,
  };
}

/**
 * '31,152 pcs · 1,298 box' — both readings of one quantity, side by side.
 *
 * Client's choice on 28 Sep: neither number hidden. The godown counts in boxes,
 * Business Central counts in pieces, and a screen showing only one of them
 * makes somebody do the arithmetic in their head against a figure they cannot
 * check.
 *
 * Falls back to the piece count alone when there is no conversion, rather than
 * printing '0 box' — which would say something false about an item that simply
 * has no pack.
 */
export function piecesAndPacks(pieces: number | null | undefined, pack: PackInfo): string {
  if (pieces === null || pieces === undefined) return '—';
  const b = packBreakdown(pieces, pack);
  const base = `${pieces.toLocaleString('en-IN')} ${b.baseUom}`;
  if (!b.convertible) return base;

  // The remainder is stated only when there is one. "1,298 box + 0 pcs" invites
  // a reader to wonder what the zero is for.
  const packPart = b.loose > 0
    ? `${b.packs.toLocaleString('en-IN')} ${b.packUom} + ${b.loose} ${b.baseUom}`
    : `${b.packs.toLocaleString('en-IN')} ${b.packUom}`;
  return `${base} · ${packPart}`;
}
